/**
 * CEMatrixEditor — implements `Capexara Process Industry_ProcessPlant_Complete_Architecture.md` §6.3 F.3.
 *
 * Cause & Effect matrix — cause rows (inputs: trips/alarms/detectors) on the
 * left, effect columns (outputs: SDVs/motors/alarms) on the top. Cells accept:
 *   X  = direct trip
 *   T10 = time-delayed trip (10 s)
 *   T30 = time-delayed trip (30 s)
 *   A  = alarm only
 *   -  = no action
 *
 * Persisted at `pdo.safety.ce_matrix` via /api/projects/{id}/ce-matrix.
 */
import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, Download } from "lucide-react";
import api, { formatErr } from "../lib/api";

const VALUES = ["-", "X", "T10", "T30", "A"];
const VALUE_CLASS = {
  "X":   { bg: "rgba(239,68,68,0.25)",  color: "#fca5a5" },
  "T10": { bg: "rgba(249,115,22,0.25)", color: "#fdba74" },
  "T30": { bg: "rgba(234,179,8,0.25)",  color: "#fde047" },
  "A":   { bg: "rgba(59,130,246,0.25)", color: "#93c5fd" },
  "-":   { bg: "transparent",            color: "var(--pf-text-dim)" },
};
const SECTIONS = ["esd_l1", "esd_l2", "esd_l3", "f_and_g", "process_trips", "compressor"];
const SECTION_LABELS = {
  "esd_l1": "ESD Level 1",
  "esd_l2": "ESD Level 2",
  "esd_l3": "ESD Level 3",
  "f_and_g": "Fire & Gas",
  "process_trips": "Process Trips",
  "compressor": "Compressor",
};

export default function CEMatrixEditor({ projectId }) {
  const [inputs, setInputs] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [cells, setCells] = useState([]);     // [{input_tag, output_tag, value, section, voting}]
  const [activeSection, setActiveSection] = useState("esd_l1");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/ce-matrix`);
      setInputs(r.data?.inputs || []);
      setOutputs(r.data?.outputs || []);
      setCells(r.data?.cells || []);
    } catch (e) {
      setToast({ kind: "err", text: formatErr(e?.response?.data?.detail) || e.message });
    } finally { setLoading(false); }
  };

  const show = (text, kind = "ok") => {
    setToast({ text, kind });
    setTimeout(() => setToast(null), 3500);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/ce-matrix`, {
        inputs, outputs, cells, sections: SECTIONS,
      });
      show(`Saved — ${cells.filter((c) => c.value !== "-" && c.value).length} active cells across ${inputs.length} inputs / ${outputs.length} outputs`);
    } catch (e) { show(formatErr(e?.response?.data?.detail) || e.message, "err"); }
    finally { setSaving(false); }
  };

  const addInput = () => {
    const tag = prompt("Input tag (e.g. PSH-101A, LSL-201, GD-305)?");
    if (!tag?.trim()) return;
    setInputs((xs) => [...xs, { tag: tag.trim(), description: "", type: "alarm" }]);
  };
  const addOutput = () => {
    const tag = prompt("Output tag (e.g. SDV-101, ESD-P-101A, ALR-HIGH)?");
    if (!tag?.trim()) return;
    setOutputs((xs) => [...xs, { tag: tag.trim(), description: "", kind: "valve" }]);
  };
  const removeInput = (tag) => {
    setInputs((xs) => xs.filter((i) => i.tag !== tag));
    setCells((cs) => cs.filter((c) => c.input_tag !== tag));
  };
  const removeOutput = (tag) => {
    setOutputs((xs) => xs.filter((o) => o.tag !== tag));
    setCells((cs) => cs.filter((c) => c.output_tag !== tag));
  };
  const cellValue = (inputTag, outputTag) => {
    const c = cells.find((c) => c.input_tag === inputTag && c.output_tag === outputTag && (c.section || "esd_l1") === activeSection);
    return c?.value || "-";
  };
  const cycleCell = (inputTag, outputTag) => {
    setCells((cs) => {
      const idx = cs.findIndex((c) => c.input_tag === inputTag && c.output_tag === outputTag && (c.section || "esd_l1") === activeSection);
      if (idx < 0) {
        return [...cs, { input_tag: inputTag, output_tag: outputTag, section: activeSection, value: "X" }];
      }
      const cur = cs[idx];
      const next = VALUES[(VALUES.indexOf(cur.value) + 1) % VALUES.length];
      const copy = [...cs];
      if (next === "-") copy.splice(idx, 1);
      else copy[idx] = { ...cur, value: next };
      return copy;
    });
  };

  const exportCsv = () => {
    const header = ["Input Tag", "Description", ...outputs.map((o) => o.tag)];
    const rows = inputs.map((inp) => [
      inp.tag, inp.description || "",
      ...outputs.map((out) => cellValue(inp.tag, out.tag)),
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${(v ?? "").toString().replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `CE_Matrix_${activeSection}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: 20 }}><Loader2 className="pf-spin" size={16}/> Loading…</div>;

  const visibleCells = cells.filter((c) => (c.section || "esd_l1") === activeSection);
  const activeCount = visibleCells.filter((c) => c.value && c.value !== "-").length;

  return (
    <div data-testid="ce-matrix-editor" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <h2 className="pf-heading" style={{ margin: 0 }}>Cause &amp; Effect Matrix</h2>
        <span className="pf-overline" style={{ color: "var(--pf-text-dim)", margin: 0 }}>Stage F.3 · IEC 61511</span>
        <div style={{ flex: 1 }}/>
        <button data-testid="ce-matrix-add-input" className="btn btn-ghost" onClick={addInput}><Plus size={12}/> Add Input (Cause)</button>
        <button data-testid="ce-matrix-add-output" className="btn btn-ghost" onClick={addOutput}><Plus size={12}/> Add Output (Effect)</button>
        <button data-testid="ce-matrix-export-csv" className="btn btn-ghost" onClick={exportCsv}><Download size={12}/> CSV</button>
        <button data-testid="ce-matrix-save" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="pf-spin" size={12}/> : <Save size={12}/>} Save
        </button>
      </div>

      <div style={{ display: "flex", gap: 4, fontSize: 11, flexWrap: "wrap" }}>
        {SECTIONS.map((s) => (
          <button
            key={s}
            data-testid={`ce-section-${s}`}
            onClick={() => setActiveSection(s)}
            style={{
              padding: "4px 10px", fontSize: 11,
              background: activeSection === s ? "var(--pf-accent)" : "var(--pf-surface-2)",
              color: activeSection === s ? "#0E1218" : "var(--pf-text)",
              border: "1px solid var(--pf-border)",
              fontWeight: activeSection === s ? 700 : 500,
              cursor: "pointer",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >{SECTION_LABELS[s]}</button>
        ))}
      </div>

      <div style={{ fontSize: 11, color: "var(--pf-text-dim)", display: "flex", gap: 16, flexWrap: "wrap" }}>
        <span><b style={{ color: "#fca5a5" }}>X</b> trip</span>
        <span><b style={{ color: "#fdba74" }}>T10</b> 10 s delayed</span>
        <span><b style={{ color: "#fde047" }}>T30</b> 30 s delayed</span>
        <span><b style={{ color: "#93c5fd" }}>A</b> alarm only</span>
        <span style={{ marginLeft: "auto" }}>{activeCount} active cells in this section · {inputs.length} inputs × {outputs.length} outputs</span>
      </div>

      {(inputs.length === 0 || outputs.length === 0) ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--pf-text-dim)", border: "1px dashed var(--pf-border)" }}>
          Add at least one Input (cause) and one Output (effect) to build the matrix.
        </div>
      ) : (
        <div style={{ overflow: "auto", border: "1px solid var(--pf-border)" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11, minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...thCol, minWidth: 140, left: 0, position: "sticky", zIndex: 2, background: "var(--pf-surface)" }}>CAUSE \ EFFECT</th>
                {outputs.map((out) => (
                  <th key={out.tag} style={thCol} data-testid={`ce-output-col-${out.tag}`}>
                    <div style={{ transform: "rotate(-45deg)", whiteSpace: "nowrap", padding: "18px 2px", minWidth: 30 }}>
                      <span style={{ color: "var(--pf-accent)", fontFamily: "'JetBrains Mono', monospace" }}>{out.tag}</span>
                    </div>
                    <button onClick={() => removeOutput(out.tag)}
                             style={{ fontSize: 9, padding: "1px 4px", background: "transparent", color: "#ef4444", border: "none", cursor: "pointer" }}>×</button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {inputs.map((inp) => (
                <tr key={inp.tag} data-testid={`ce-input-row-${inp.tag}`}>
                  <td style={{ ...tdCell, left: 0, position: "sticky", background: "var(--pf-surface)", zIndex: 1, textAlign: "left", fontFamily: "'JetBrains Mono', monospace" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ color: "var(--pf-spark)" }}>{inp.tag}</span>
                      <button onClick={() => removeInput(inp.tag)}
                               style={{ fontSize: 9, padding: "0 6px", background: "transparent", color: "#ef4444", border: "none", cursor: "pointer", marginLeft: "auto" }}>×</button>
                    </div>
                  </td>
                  {outputs.map((out) => {
                    const v = cellValue(inp.tag, out.tag);
                    const s = VALUE_CLASS[v];
                    return (
                      <td key={out.tag}
                          data-testid={`ce-cell-${inp.tag}-${out.tag}`}
                          onClick={() => cycleCell(inp.tag, out.tag)}
                          style={{
                            ...tdCell,
                            background: s.bg, color: s.color,
                            cursor: "pointer",
                            fontWeight: v === "-" ? 400 : 700,
                            minWidth: 36,
                          }}
                          title="Click to cycle: - → X → T10 → T30 → A → -"
                      >{v === "-" ? "" : v}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div data-testid={`ce-toast-${toast.kind}`} style={{
          position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)",
          background: toast.kind === "ok" ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.18)",
          border: `1px solid ${toast.kind === "ok" ? "#22c55e" : "#ef4444"}`,
          color: toast.kind === "ok" ? "#86efac" : "#fca5a5",
          padding: "8px 16px", fontSize: 12, zIndex: 10001,
        }}>{toast.text}</div>
      )}
    </div>
  );
}

const thCol = { padding: "4px 6px", borderBottom: "1px solid var(--pf-border)", borderRight: "1px solid var(--pf-border)", background: "var(--pf-surface-2)", textAlign: "center", fontWeight: 600, color: "var(--pf-text-dim)", fontSize: 10, verticalAlign: "bottom" };
const tdCell = { padding: "6px 8px", borderBottom: "1px solid var(--pf-border)", borderRight: "1px solid var(--pf-border)", textAlign: "center" };
